import { reactive, type UnwrapNestedRefs } from 'vue';
import type { GIMovie } from 'src/models/geoItem';
import type {
  BothLeftRight,
  LeftRight,
  MoviePlayerCompareModeState,
} from 'src/models/moviePlayer';

interface Deps {
  setCurrentMovie: (lr: LeftRight, movie: GIMovie, movieIdx: number, playOffsetMsec: number) => Promise<void>;
  pauseUI: (lr_: BothLeftRight) => void;
  playUI: (lr_: BothLeftRight) => void;
}

interface MoviePlayerCompareModeSeekContext {
  seekMovie: (lr: LeftRight, offsetMsec: number) => Promise<void>;
  seekBySeekBar: (lr: LeftRight, offsetMsec: number) => void;
  startTimeTextEdit: () => Promise<void>;
  seekByTimeText: (lr: LeftRight, timePoint: { dt: Date; dateDisp: string; timeDisp: string }) => Promise<void>;
}

// useMoviePlayerCompareModePlayback.tsから分割したシーク（早送り位置指定）関連の関数。
// 動画切替はplayback.setCurrentMovieに委譲し、vjsインスタンス自体は保持しない。
export function useMoviePlayerCompareModeSeek(
  state: UnwrapNestedRefs<MoviePlayerCompareModeState>,
  deps: Deps,
): UnwrapNestedRefs<MoviePlayerCompareModeSeekContext> {
  const seekMovie = async(lr: LeftRight, offsetMsec: number) => {
    state[lr].playback.isSeeking = true;
    // movieListのmovieについて順番に、offsetMsecのそのmovie内でのoffsetを算出する.
    // そのmovieのplayDuration内にoffsetが収まっていればそれが再生すべきmovie
    // となる. (最後のmovieについて、playEnd < hardEndでplayEnd以降の位置を
    // 指定されていた場合は最後のmovieを再生させたいのでそのようにしてある)
    let matchedMovie;
    let matchedMovieIdx;
    let matchedMoviePlayOffsetMsec = 0;
    const currentMl = state[lr].movieList.listSelection.currentMovieList;
    if (!currentMl) return;

    for (let i = 0, len = currentMl.movies.length; i < len; i++) {
      const movie = currentMl.movies[i];
      matchedMovie = movie;
      matchedMovieIdx = i;
      matchedMoviePlayOffsetMsec = offsetMsec - (matchedMovie.accumMsec || 0);
      if (movie.playDurationMsec === undefined || matchedMoviePlayOffsetMsec < movie.playDurationMsec) {
        break;
      }
    }
    if (matchedMovie && matchedMovieIdx !== undefined) {
      await deps.setCurrentMovie(
        lr,
        matchedMovie,
        matchedMovieIdx,
        matchedMoviePlayOffsetMsec,
      );
    }
    state[lr].playback.isSeeking = false;
  };
  const seekBySeekBar = (lr: LeftRight, offsetMsec: number) => {
    state[lr].playback.seek.current = offsetMsec;
    seekMovie(lr, offsetMsec);
  };
  const startTimeTextEdit = async() => {
    // 両方停止する
    deps.pauseUI('left');
    deps.pauseUI('right');
  };
  const seekByTimeText = async(lr: LeftRight, timePoint: { dt: Date; dateDisp: string; timeDisp: string }) => {
    state[lr].playback.isSeeking = true;
    // movieListの開始と指定された時刻の差に、seek.minのずらし分を考慮
    let tmpOffset = timePoint.dt.getTime() - (state[lr].movieList.listSelection.currentMovieList?.hardStartTs?.getTime() || 0);
    tmpOffset = Math.max(tmpOffset, 0);
    tmpOffset = Math.min(tmpOffset, (state[lr].movieList.listSelection.currentMovieList?.fullDurationMsec || 0));
    const offsetMsec = tmpOffset + state[lr].playback.seek.min;
    await seekMovie(lr, offsetMsec);
    // 両方再生する
    deps.playUI('left');
    deps.playUI('right');
    state[lr].playback.isSeeking = false;
  };

  return reactive({ seekMovie, seekBySeekBar, startTimeTextEdit, seekByTimeText });
}
